export function createIdentifyService({ events, store, ui, map }) {
  async function fetchJsonWithTimeout(url, timeoutMs = 12000) {
    const ctl = new AbortController();
    const timer = setTimeout(() => {
      try { ctl.abort(); } catch {}
    }, Math.max(1000, Number(timeoutMs) || 12000));
    try {
      const r = await fetch(url, { signal: ctl.signal });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return await r.json();
    } finally {
      clearTimeout(timer);
    }
  }

  function parseBlueTopoElevation(payload) {
    const features = Array.isArray(payload?.features) ? payload.features : [];
    for (let i = 0; i < features.length; i += 1) {
      const p = features[i]?.properties;
      if (!p || typeof p !== "object") continue;
      const cand = [
        p.ELEVATION,
        p.elevation,
        p.value,
        p.GRAY_INDEX,
        p.GRAYINDEX,
      ];
      for (let j = 0; j < cand.length; j += 1) {
        const n = Number(cand[j]);
        if (Number.isFinite(n)) return n;
      }
    }
    return null;
  }

  async function sampleDepth(lon, lat, source = "etopo") {
    const src = String(source || "etopo").toLowerCase();
    if (!Number.isFinite(Number(lon)) || !Number.isFinite(Number(lat))) {
      throw new Error("sampleDepth requires numeric lon/lat");
    }

    if (src === "gebco") {
      const base = "https://tiles.arcgis.com/tiles/C8EMgrsFcRFL6LrL/arcgis/rest/services/GEBCO_elevation_wgs84/ImageServer/identify";
      const url = `${base}?${new URLSearchParams({
        f: "json",
        geometry: JSON.stringify({ x: Number(lon), y: Number(lat), spatialReference: { wkid: 4326 } }),
        geometryType: "esriGeometryPoint",
        sr: "4326",
        returnGeometry: "false",
      })}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`GEBCO identify failed (${r.status})`);
      const j = await r.json();
      const v = j?.value ?? j?.properties?.value ?? j?.pixelValue;
      const depth = Number(v);
      return { lon: Number(lon), lat: Number(lat), source: src, depth_m: Number.isFinite(depth) ? depth : null };
    }

    if (src === "bluetopo") {
      const latNum = Number(lat);
      const lonNum = Number(lon);
      const padDeg = 0.02;
      const bbox = `${latNum - padDeg},${lonNum - padDeg},${latNum + padDeg},${lonNum + padDeg}`;
      const url = `https://nowcoast.noaa.gov/geoserver/hydrographic/bluetopo/ows?${new URLSearchParams({
        SERVICE: "WMS",
        VERSION: "1.3.0",
        REQUEST: "GetFeatureInfo",
        LAYERS: "bathymetry",
        QUERY_LAYERS: "bathymetry",
        STYLES: "",
        CRS: "EPSG:4326",
        BBOX: bbox,
        WIDTH: "101",
        HEIGHT: "101",
        I: "50",
        J: "50",
        INFO_FORMAT: "application/json",
        FEATURE_COUNT: "3",
      })}`;
      const j = await fetchJsonWithTimeout(url, 12000);
      const elev = parseBlueTopoElevation(j);
      return { lon: lonNum, lat: latNum, source: src, depth_m: Number.isFinite(elev) ? elev : null };
    }

    const base = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer";
    const g = encodeURIComponent(JSON.stringify({ x: Number(lon), y: Number(lat), spatialReference: { wkid: 4326 } }));
    const r = await fetch(`${base}/identify?f=json&geometry=${g}&geometryType=esriGeometryPoint&sr=4326&returnGeometry=false`);
    if (!r.ok) throw new Error(`ETOPO identify failed (${r.status})`);
    const j = await r.json();
    const v = j?.value ?? j?.properties?.value ?? j?.pixelValue;
    const depth = Number(v);
    return { lon: Number(lon), lat: Number(lat), source: src, depth_m: Number.isFinite(depth) ? depth : null };
  }

  return { sampleDepth };
}
